/**
 * AdminLayout — shared left rail + <Outlet /> for all /admin/* routes.
 *
 * Navigation uses NavLink so the active tab is derived from the current URL
 * instead of local state. The footer (profile, manual, view-as, logout) are
 * kept from the original AdminPanel shell.
 *
 * Mobile: a hamburger button in the top bar opens a left-side drawer with the
 * same nav items and footer actions as the desktop rail. Closes on item tap,
 * backdrop tap, Escape key, or route change.
 */
import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  FileText,
  Users,
  Building2,
  Palette,
  LogOut,
  User,
  HelpCircle,
  Menu,
  X,
  AlertTriangle,
} from "lucide-react";
import type { ComponentType } from "react";
import { useAuth } from "@/features/auth/index.js";
import { Button } from "@/shared/ui/button.js";
import { BrandLogo } from "@/shared/ui/BrandLogo.js";
import { cn } from "@/shared/lib/utils.js";
import { getStorageStatus } from "../services/storage-status.service.js";

const NAV_ITEMS: {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}[] = [
    { to: "/admin", label: "Informes", icon: FileText, end: true },
    { to: "/admin/usuarios", label: "Usuarios", icon: Users },
    { to: "/admin/clientes", label: "Clientes", icon: Building2 },
    { to: "/admin/marca", label: "Marca", icon: Palette },
  ];

const railLabelClass =
  "font-mono text-[0.5rem] font-medium uppercase tracking-[0.04em] leading-none";

const drawerLabelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em]";

function RailNavLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      title={label}
      className={({ isActive }) =>
        cn(
          "flex w-16 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 transition-colors",
          isActive
            ? "bg-brand-accent text-brand-accent-foreground"
            : "text-background/50 hover:bg-background/10 hover:text-background",
        )
      }
    >
      <Icon className="size-5" aria-hidden />
      <span className={railLabelClass}>{label}</span>
    </NavLink>
  );
}

function RailIconButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        "flex size-12 items-center justify-center rounded-xl transition-colors",
        active
          ? "bg-brand-accent text-brand-accent-foreground"
          : "text-background/50 hover:bg-background/10 hover:text-background",
      )}
    >
      <Icon className="size-5" aria-hidden />
    </button>
  );
}

export function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Panel-wide banner: true when the storage provider (Cloudinary/R2) is degraded. */
  const [storageDegraded, setStorageDegraded] = useState(false);

  // Close drawer on route change (NavLink click already calls closeDrawer,
  // but this ensures close even when the route changes from other sources).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // On-mount check of storage status — persistent across the whole admin panel,
  // not just one report/view. Fails silently: this must never block the panel
  // (e.g. if /storage/status 404s because the backend isn't deployed yet).
  useEffect(() => {
    getStorageStatus()
      .then(({ degraded }) => setStorageDegraded(degraded))
      .catch(() => {});
  }, []);

  // Escape key closes drawer
  useEffect(() => {
    if (!drawerOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen]);

  function closeDrawer() {
    setDrawerOpen(false);
  }

  return (
    <div className="paper-surface paper-grid relative flex h-screen flex-col overflow-hidden md:flex-row">
      {/* Brand accent stripe */}
      <div aria-hidden className="absolute inset-x-0 top-0 z-30 h-1 bg-brand-accent" />

      {/* ── Mobile drawer (md:hidden) ──────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            aria-hidden
            onClick={closeDrawer}
          />

          {/* Slide-in panel */}
          <div
            id="admin-drawer"
            role="dialog"
            aria-label="Menú de navegación"
            aria-modal="true"
            className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-[#fdfcf9] shadow-2xl md:hidden"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between border-b border-[#ddd9cc] px-5 py-4">
              <BrandLogo height={24} />
              <button
                type="button"
                onClick={closeDrawer}
                aria-label="Cerrar menú"
                className="rounded-md p-1.5 text-[#474741] transition-colors hover:bg-[#f4f3ee] hover:text-[#1c1b1b]"
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>

            {/* Nav items — same list as the desktop rail */}
            <nav
              className="flex flex-1 flex-col gap-1 overflow-y-auto p-3"
              aria-label="Admin navigation"
            >
              {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={closeDrawer}
                  className={({ isActive }) =>
                    cn(
                      "flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors",
                      isActive
                        ? "bg-brand-accent text-brand-accent-foreground"
                        : "text-[#474741] hover:bg-[#f4f3ee] hover:text-[#1c1b1b]",
                    )
                  }
                >
                  <Icon className="size-5" aria-hidden />
                  <span className={drawerLabelClass}>{label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Footer actions — mirrors the desktop rail footer */}
            <div className="flex flex-col gap-1 border-t border-[#ddd9cc] p-3">
              <button
                type="button"
                onClick={() => { navigate("/admin/perfil"); closeDrawer(); }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors",
                  location.pathname === "/admin/perfil"
                    ? "bg-brand-accent text-brand-accent-foreground"
                    : "text-[#474741] hover:bg-[#f4f3ee] hover:text-[#1c1b1b]",
                )}
              >
                <User className="size-5" aria-hidden />
                <span className={drawerLabelClass}>Mi perfil</span>
              </button>

              <button
                type="button"
                onClick={() => { navigate("/admin/manual"); closeDrawer(); }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors",
                  location.pathname === "/admin/manual"
                    ? "bg-brand-accent text-brand-accent-foreground"
                    : "text-[#474741] hover:bg-[#f4f3ee] hover:text-[#1c1b1b]",
                )}
              >
                <HelpCircle className="size-5" aria-hidden />
                <span className={drawerLabelClass}>Manual</span>
              </button>

              <button
                type="button"
                onClick={() => { logout(); closeDrawer(); }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-[#474741] transition-colors hover:bg-[#f4f3ee] hover:text-[#1c1b1b]"
              >
                <LogOut className="size-5" aria-hidden />
                <span className={drawerLabelClass}>Salir</span>
              </button>

            </div>
          </div>
        </>
      )}

      {/* ── Left rail (desktop only) ─────────────────────────────────── */}
      <nav
        aria-label="Admin navigation"
        className="relative z-20 hidden w-20 shrink-0 flex-col items-center gap-6 border-r border-border bg-foreground py-8 md:flex"
      >
        {NAV_ITEMS.map((item) => (
          <RailNavLink key={item.to} {...item} />
        ))}

        <div className="mt-auto flex flex-col items-center gap-5">
          <RailIconButton
            icon={User}
            label="Mi perfil"
            active={location.pathname === "/admin/perfil"}
            onClick={() => navigate("/admin/perfil")}
          />
          <RailIconButton
            icon={HelpCircle}
            label="Manual"
            active={location.pathname === "/admin/manual"}
            onClick={() => navigate("/admin/manual")}
          />
          <RailIconButton icon={LogOut} label="Salir" onClick={logout} />
        </div>
      </nav>

      {/* ── Content area ─────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Abrir menú"
              aria-expanded={drawerOpen}
              aria-controls="admin-drawer"
              onClick={() => setDrawerOpen(true)}
              className="rounded-md p-1.5 text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
            >
              <Menu className="size-5" aria-hidden />
            </button>
            <BrandLogo height={28} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="gap-2 rounded-full font-mono text-[0.625rem] uppercase tracking-[0.12em]"
            >
              <LogOut className="size-4" aria-hidden />
              Salir
            </Button>
          </div>
        </header>

        {storageDegraded && (
          <div
            role="alert"
            className="flex items-start gap-3 border-b border-amber-500/40 bg-amber-500/10 px-5 py-3"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Almacenamiento en la nube al límite. Los informes funcionan, pero las fotos nuevas
              pueden no llegar al panel hasta regularizarlo. Contactá a soporte.
            </p>
          </div>
        )}

        <Outlet />
      </div>
    </div>
  );
}
